import axios from "../API/axios";
import { startRealtimeHub, stopRealtimeHub } from "./realtimeHubService";

const inFlight = new Map();
const cache = new Map();

const TTL = {
  USER_CONFIG: 60_000,
  NOTIFICATION_COUNT: 15_000,
  MESSAGE_COUNT: 15_000,
  MESSAGE_LIST: 1_500,
  MESSAGE_LIST_COUNT: 1_500,
  OWN_COMPANIES: 60_000,
  EMOJI_DDL: 300_000,
  CONNECTION_LIST: 30_000,
  USER_ALL_CHAT: 1_500,
  CHECK_CONNECTION: 30_000,
  SHORT_MUTUAL: 120_000,
  RECOMMENDATION_LIST: 60_000,
  SYNC_ACCOUNT: 120_000,
  OWN_INFO: 60_000,
  PROFILE_PERCENTAGE: 60_000,
};

const getLangKey = () => {
  const language = localStorage.getItem("Lang");
  return language === "en" ? "" : language;
};

const getSessionId = () => localStorage.getItem("sessionId");

const buildCacheKey = (endpoint, params = {}) =>
  `${endpoint}:${JSON.stringify(params)}`;

const invalidateByPrefix = (prefix) => {
  for (const key of [...cache.keys(), ...inFlight.keys()]) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
      inFlight.delete(key);
    }
  }
};

const fetchWithDedup = async (
  cacheKey,
  fetcher,
  { ttl = 60_000, forceRefresh = false } = {}
) => {
  if (!forceRefresh) {
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < ttl) {
      return cached.data;
    }
  }

  if (inFlight.has(cacheKey)) {
    return inFlight.get(cacheKey);
  }

  if (forceRefresh) {
    cache.delete(cacheKey);
  }

  const promise = fetcher()
    .then((data) => {
      cache.set(cacheKey, { data, timestamp: Date.now() });
      return data;
    })
    .finally(() => {
      inFlight.delete(cacheKey);
    });

  inFlight.set(cacheKey, promise);
  return promise;
};

export const clearApiCache = () => {
  cache.clear();
  inFlight.clear();
};

export const invalidateUserConfig = () =>
  invalidateByPrefix("User/GetUserConfig:");

export const invalidateNotificationCount = () =>
  invalidateByPrefix("General/GetUnReadNotificationCount:");

export const resetNotificationCountForLogin = () => {
  try {
    localStorage.removeItem("NotificationCountFetched");
    localStorage.removeItem("NotificationCountData");
  } catch (e) {
    // ignore storage errors
  }
  invalidateNotificationCount();
};

const dispatchNotificationCountUpdated = (count) => {
  window.dispatchEvent(
    new CustomEvent("notificationCountUpdated", { detail: { count } })
  );
};

export const resetMessageCountForLogin = () => {
  try {
    localStorage.removeItem("MsgCountData");
    localStorage.removeItem("MsgCountText");
  } catch (e) {
    // ignore storage errors
  }
  invalidateMessageCount();
};

const dispatchMessageCountUpdated = (count) => {
  window.dispatchEvent(
    new CustomEvent("messageCountUpdated", { detail: { count } })
  );
};

export const completeLoginNotificationFetch = async () => {
  resetNotificationCountForLogin();
  resetMessageCountForLogin();
  try {
    const [notifCount, msgCount] = await Promise.all([
      getUnreadNotificationCount({ forceRefresh: true }),
      getUnreadMessagesCount({ forceRefresh: true }),
    ]);
    if (notifCount != null) {
      dispatchNotificationCountUpdated(notifCount);
    }
    if (msgCount != null) {
      dispatchMessageCountUpdated(msgCount);
    }
    return notifCount;
  } catch (e) {
    return null;
  } finally {
    window.dispatchEvent(new Event("userLoggedIn"));
    try {
      await startRealtimeHub();
    } catch {
      // hub connect is best-effort after login
    }
  }
};

export const markConversationAsRead = async (toUserUid) => {
  const sessionId = getSessionId();
  if (!sessionId || !toUserUid) return null;

  const response = await axios.post("Message/MarkConversationAsRead", {
    deviceType: "Web",
    sessionId,
    toUserUid,
    langKey: getLangKey(),
  });

  invalidateMessageCount();
  return response.data;
};

export const readAllChat = async ({ toUserUid, msgID }) => {
  const sessionId = getSessionId();
  if (!sessionId || !toUserUid || !msgID) return null;

  const response = await axios.post("Message/ReadAllChat", {
    deviceType: "Web",
    sessionId,
    msgID,
    toUserUid,
    langKey: getLangKey(),
  });

  console.warn("[ReadReceipt] API ReadAllChat OK", {
    toUserUid,
    msgID,
    response: response.data,
  });

  invalidateMessageCount();
  invalidateMessageList();
  invalidateMessageListCounts();
  return response.data;
};

export const readSingleChat = async (chatID) => {
  const sessionId = getSessionId();
  if (!sessionId || !chatID) return null;

  const response = await axios.post("Message/ReadChat", {
    deviceType: "Web",
    sessionId,
    chatID,
    langKey: getLangKey(),
  });

  console.warn("[ReadReceipt] API ReadChat OK", {
    chatID,
    response: response.data,
  });

  invalidateMessageList();
  invalidateMessageListCounts();
  return response.data;
};

export const disconnectRealtimeHub = () => stopRealtimeHub();

export const invalidateMessageCount = () =>
  invalidateByPrefix("Message/AllUnreadMsgsCount:");

export const invalidateOwnCompaniesList = () =>
  invalidateByPrefix("Company/GetOwnCompaniesList:");

export const invalidateOwnInfo = () =>
  invalidateByPrefix("UserProfile/GetOwnInfo:");

export const invalidateConnectionList = () =>
  invalidateByPrefix("Connection/GetConnectionList:");

export const invalidateUserAllChat = () =>
  invalidateByPrefix("Message/GetUserAllChat:");

export const getUserAllChat = async (
  payload,
  { forceRefresh = false } = {}
) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const fullPayload = {
    deviceType: "Web",
    sessionId,
    ...payload,
  };
  const cacheKey = buildCacheKey("Message/GetUserAllChat", fullPayload);

  return fetchWithDedup(
    cacheKey,
    async () => axios.post("Message/GetUserAllChat", fullPayload),
    { ttl: TTL.USER_ALL_CHAT, forceRefresh }
  );
};

export const invalidateMessageList = () =>
  invalidateByPrefix("Message/GetMessageList:");

export const invalidateMessageListCounts = () =>
  invalidateByPrefix("Message/GetMessageCount:");

export const getMessageList = async ({
  forceRefresh = false,
  pageNumber,
  pageSize,
  topCount,
} = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const fullPayload = {
    deviceType: "Web",
    sessionId,
  };
  if (pageNumber > 0 && pageSize > 0) {
    fullPayload.pageNumber = pageNumber;
    fullPayload.pageSize = pageSize;
  }
  if (topCount > 0) {
    fullPayload.topCount = topCount;
  }
  const cacheKey = buildCacheKey("Message/GetMessageList", fullPayload);

  return fetchWithDedup(
    cacheKey,
    async () => axios.post("Message/GetMessageList", fullPayload),
    { ttl: TTL.MESSAGE_LIST, forceRefresh }
  );
};

export const getMessageCounts = async ({ forceRefresh = false } = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const fullPayload = {
    deviceType: "Web",
    sessionId,
    langKey,
  };
  const cacheKey = buildCacheKey("Message/GetMessageCount", fullPayload);

  return fetchWithDedup(
    cacheKey,
    async () =>
      axios.post("Message/GetMessageCount?Archive=true", fullPayload),
    { ttl: TTL.MESSAGE_LIST_COUNT, forceRefresh }
  );
};

let messagePageInitialLoad = null;

export const loadMessagePageInitialData = () => {
  if (messagePageInitialLoad) {
    return messagePageInitialLoad;
  }

  messagePageInitialLoad = Promise.all([
    getMessageList(),
    getMessageCounts(),
  ]).finally(() => {
    setTimeout(() => {
      messagePageInitialLoad = null;
    }, 3000);
  });

  return messagePageInitialLoad;
};

export const invalidateCheckConnection = (userUID) => {
  if (userUID) {
    const sessionId = getSessionId();
    const langKey = getLangKey();
    const cacheKey = buildCacheKey("Connection/CheckConnection", {
      sessionId,
      userUID,
      langKey,
    });
    cache.delete(cacheKey);
    inFlight.delete(cacheKey);
  } else {
    invalidateByPrefix("Connection/CheckConnection:");
  }
};

export const invalidateShortMutualInfo = () =>
  invalidateByPrefix("UserFollow/GetShortMutualUserInfo:");

export const getUserConfig = async ({ forceRefresh = false } = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("User/GetUserConfig", { sessionId, langKey });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("User/GetUserConfig", {
        deviceType: "Web",
        sessionId,
        langKey,
      });
      return res.data.responseData;
    },
    { ttl: TTL.USER_CONFIG, forceRefresh }
  );
};

export const getUnreadNotificationCount = async ({
  forceRefresh = false,
} = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("General/GetUnReadNotificationCount", {
    sessionId,
    langKey,
  });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("General/GetUnReadNotificationCount", {
        deviceType: "Web",
        sessionId,
        langKey,
      });
      const count = res.data.responseData;
      localStorage.setItem("NotificationCountData", count);
      return count;
    },
    { ttl: TTL.NOTIFICATION_COUNT, forceRefresh }
  );
};

export const getUnreadMessagesCount = async ({ forceRefresh = false } = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("Message/AllUnreadMsgsCount", {
    sessionId,
    langKey,
  });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("Message/AllUnreadMsgsCount", {
        deviceType: "Web",
        sessionId,
        langKey,
      });
      const responseData = res.data.responseData;
      const count =
        typeof responseData === "number"
          ? responseData
          : typeof responseData === "string"
          ? Number(responseData.split("/")[0] || 0)
          : Number(responseData?.totalUnreadCount ?? responseData?.TotalUnreadCount ?? 0);
      localStorage.setItem("MsgCountData", count);
      if (responseData?.unreadCountText || responseData?.UnreadCountText) {
        localStorage.setItem("MsgCountText", responseData.unreadCountText ?? responseData.UnreadCountText);
      }
      return count;
    },
    { ttl: TTL.MESSAGE_COUNT, forceRefresh }
  );
};

export const getOwnCompaniesList = async ({ forceRefresh = false } = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("Company/GetOwnCompaniesList", {
    sessionId,
    langKey,
  });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("Company/GetOwnCompaniesList", {
        sessionId,
        deviceType: "Web",
        langKey,
      });
      return res.data.responseData;
    },
    { ttl: TTL.OWN_COMPANIES, forceRefresh }
  );
};

export const getEmojiDDL = async (langKey, { forceRefresh = false } = {}) => {
  const language = langKey ?? localStorage.getItem("Lang") ?? "en";
  const cacheKey = buildCacheKey("Master/GetEmojiDDL", { language });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.get(`Master/GetEmojiDDL?LangKey=${language}`);
      return res.data.responseData;
    },
    { ttl: TTL.EMOJI_DDL, forceRefresh }
  );
};

export const getConnectionList = async (
  payload,
  { forceRefresh = false } = {}
) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const fullPayload = {
    deviceType: "Web",
    sessionId,
    langKey,
    ...payload,
  };
  const cacheKey = buildCacheKey("Connection/GetConnectionList", fullPayload);

  return fetchWithDedup(
    cacheKey,
    async () => axios.post("Connection/GetConnectionList", fullPayload),
    { ttl: TTL.CONNECTION_LIST, forceRefresh }
  );
};

export const checkConnection = async (
  userUID,
  { forceRefresh = false } = {}
) => {
  const sessionId = getSessionId();
  if (!sessionId || !userUID) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("Connection/CheckConnection", {
    sessionId,
    userUID,
    langKey,
  });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("Connection/CheckConnection", {
        deviceType: "Web",
        sessionId,
        userUID,
        langKey,
      });
      return res.data;
    },
    { ttl: TTL.CHECK_CONNECTION, forceRefresh }
  );
};

export const getShortMutualUserInfo = async (
  userUid,
  { forceRefresh = false } = {}
) => {
  const sessionId = getSessionId();
  if (!sessionId || !userUid) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("UserFollow/GetShortMutualUserInfo", {
    sessionId,
    userUid,
    langKey,
  });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("UserFollow/GetShortMutualUserInfo", {
        deviceType: "Web",
        sessionId,
        userUid,
        langKey,
      });
      return res.data.responseData;
    },
    { ttl: TTL.SHORT_MUTUAL, forceRefresh }
  );
};

export const getRecommendationList = async (
  payload,
  { forceRefresh = false } = {}
) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const fullPayload = {
    deviceType: "Web",
    sessionId,
    langKey,
    ...payload,
  };
  const cacheKey = buildCacheKey(
    "UserFollow/GetRecommendationList",
    fullPayload
  );

  return fetchWithDedup(
    cacheKey,
    async () => axios.post("UserFollow/GetRecommendationList", fullPayload),
    { ttl: TTL.RECOMMENDATION_LIST, forceRefresh }
  );
};

export const getOwnInfo = async ({ forceRefresh = false } = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("UserProfile/GetOwnInfo", { sessionId, langKey });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("/UserProfile/GetOwnInfo", {
        sessionId,
        deviceType: "Web",
        langKey,
      });
      return res.data.responseData;
    },
    { ttl: TTL.OWN_INFO, forceRefresh }
  );
};

export const getProfilePercentage = async ({ forceRefresh = false } = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const cacheKey = buildCacheKey("UserProfile/GetProfilePercentage", { sessionId });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("UserProfile/GetProfilePercentage", {
        deviceType: "Web",
        sessionId,
      });
      return res.data.responseData;
    },
    { ttl: TTL.PROFILE_PERCENTAGE, forceRefresh }
  );
};

export const syncAccountAlgoData = async ({ forceRefresh = false } = {}) => {
  const sessionId = getSessionId();
  if (!sessionId) return null;

  const langKey = getLangKey();
  const cacheKey = buildCacheKey("Feature/SyncAccountAlgoData", {
    sessionId,
    langKey,
  });

  return fetchWithDedup(
    cacheKey,
    async () => {
      const res = await axios.post("Feature/SyncAccountAlgoData", {
        deviceType: "Web",
        sessionId,
        langKey,
      });
      return res.data.responseData;
    },
    { ttl: TTL.SYNC_ACCOUNT, forceRefresh }
  );
};

export const invalidateSyncAccountAlgoData = () =>
  invalidateByPrefix("Feature/SyncAccountAlgoData:");

export const applySyncAccountAlgoData = (algoData) => {
  if (!algoData) return false;

  localStorage.setItem(
    "engagedUsersMostEngagedPostIds",
    algoData.engagedUsersMostEngagedPostIds
  );
  localStorage.setItem(
    "loggedInUser_EngagedUsersIDs",
    algoData.loggedInUser_EngagedUsersIDs
  );
  localStorage.setItem(
    "peopleRecommendationsIds",
    algoData.peopleRecommendationsIds
  );
  localStorage.setItem(
    "jobRecommendationsIds",
    algoData.jobRecommendationsIds
  );
  localStorage.setItem(
    "companyRecommendationsIds",
    algoData.companyRecommendationsIds
  );
  localStorage.setItem("allPostsIds", algoData.allPostsIds);
  localStorage.setItem("connectedUsersIDs", algoData.connectedUsersIDs || null);
  localStorage.setItem(
    "followedCompaniesIDs",
    algoData.followedCompaniesIDs || null
  );
  localStorage.setItem("followedUsersIDs", algoData.followedUsersIDs || null);
  localStorage.setItem(
    "followedCompaniesPostsIds",
    algoData.followedCompaniesPostsIds || null
  );
  localStorage.setItem(
    "ownedMostEngagedPostIds",
    algoData.ownedMostEngagedPostIds
  );
  localStorage.setItem("ownedAllPostIds", algoData.ownedAllPostIds);
  localStorage.setItem("restrictedViewerToken", algoData.restrictedViewerToken);

  window.dispatchEvent(new Event("syncAccountAlgoUpdated"));
  return true;
};

export const refreshSyncAccountAlgoData = async ({ forceRefresh = true } = {}) => {
  const algoData = await syncAccountAlgoData({ forceRefresh });
  applySyncAccountAlgoData(algoData);
  return algoData;
};
